import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { Bell, Trash, CheckCircle2, RotateCcw, AlertCircle } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import logoImg from '@/assets/Sthitih_Logo.png';
import { NotificationBell } from '@/components/NotificationBell';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface NotificationItem {
  id: string;
  message: string;
  time: string;
  read: boolean;
}

const Notifications: React.FC = () => {
  const storageKey = 'sthitih_notifications_superadmin';
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = () => {
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      try {
        setNotifications(JSON.parse(localData));
      } catch (e) {
        initializeSampleNotifications();
      }
    } else {
      initializeSampleNotifications();
    }
  };

  const initializeSampleNotifications = () => {
    const samples: NotificationItem[] = [
      { id: '1', message: 'New Client Subscribed: MIT ADT University (Trial Tier)', time: '5 mins ago', read: false },
      { id: '2', message: 'Urgent Support Ticket: Payment mismatch from Southern Campus', time: '1 hr ago', read: false },
      { id: '3', message: 'Daily automated backup completed successfully', time: '1 day ago', read: true }
    ];
    setNotifications(samples);
    localStorage.setItem(storageKey, JSON.stringify(samples));
  };

  const saveToStorage = (list: NotificationItem[]) => {
    setNotifications(list);
    localStorage.setItem(storageKey, JSON.stringify(list));
  };

  const toggleRead = (id: string) => {
    const updated = notifications.map(n => n.id === id ? { ...n, read: !n.read } : n);
    saveToStorage(updated);
    toast.success('Notification status updated');
  };

  const deleteNotification = (id: string) => {
    const updated = notifications.filter(n => n.id !== id);
    saveToStorage(updated);
    toast.success('Notification deleted');
  };

  const markAllRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveToStorage(updated);
    toast.success('All notifications marked as read');
  };

  const clearAll = () => {
    saveToStorage([]);
    toast.success('All notification logs cleared');
  };

  return (
    <div className="flex min-h-screen bg-[#F7F2EA]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Notifications" />

        {/* Sleek blended header */}
        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <h1 className="text-sm sm:text-base font-black tracking-tight text-[#243238] uppercase">Notifications</h1>
          <div className="flex items-center gap-4 select-none">
            <NotificationBell type="superadmin" />
            <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
          </div>
        </header>

        {/* Content Body */}
        <main className="p-4 sm:p-8 max-w-5xl mx-auto w-full space-y-6 text-[#1E293B]">
          <div className="glass-panel p-6 rounded-2xl bg-white border border-gray-100 shadow-xs space-y-5">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#2D6A4F]" />
                <span className="font-extrabold text-sm text-[#1B4332] uppercase tracking-wider">Notifications</span>
              </div>
              {notifications.length > 0 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={markAllRead}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors border"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Mark All Read</span>
                  </button>
                  <button
                    onClick={clearAll}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-550/10 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors border border-red-100"
                  >
                    <Trash className="w-3.5 h-3.5" />
                    <span>Clear All Logs</span>
                  </button>
                </div>
              )}
            </div>

            {/* List */}
            <div className="space-y-3">
              {notifications.length === 0 ? (
                <div className="text-center py-20 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                  <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 italic text-xs font-medium">No alerts recorded.</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`p-4 rounded-xl border transition-all flex items-center justify-between ${
                      n.read
                        ? 'bg-white border-slate-200/85 hover:border-slate-305'
                        : 'bg-emerald-500/[0.03] border-emerald-500/20 hover:border-emerald-500/35 shadow-2xs'
                    }`}
                  >
                    <div className="flex items-start gap-3.5 flex-1 min-w-0 pr-4">
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-2 ${n.read ? 'bg-slate-300' : 'bg-[#2D6A4F]'}`} />
                      <div className="space-y-1">
                        <p className={`text-xs leading-relaxed ${n.read ? 'text-slate-500' : 'text-slate-800 font-bold'}`}>
                          {n.message}
                        </p>
                        <span className="text-[10px] text-slate-400 font-semibold block">{n.time}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleRead(n.id)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                        title={n.read ? 'Mark as Unread' : 'Mark as Read'}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteNotification(n.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-650 transition-colors"
                        title="Delete Alert"
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Notifications;
